import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Package,
  Users,
  DollarSign,
  ShoppingCart,
  AlertTriangle,
  RefreshCw,
  ArrowRight,
  Calendar,
  FileText,
  Download,
  Mail,
  Printer,
  MessageCircle,
  X,
  Share2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import { getBranchOverview, getBranchTrend, getTopProducts } from '../../api/analytics';
import { getBranch } from '../../api/branches';
import BlurredValue from '../../components/common/BlurredValue';
import { formatDateToISO } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const BranchDashboard = () => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canViewFinancials = ['admin', 'super_admin', 'retail_admin'].includes(user?.role);

  const [branch, setBranch] = useState(null);
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Report Modal State
  const [showReportModal, setShowReportModal] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfDataUrl, setPdfDataUrl] = useState(null);

  // Date Range State
  const [dateRangeType, setDateRangeType] = useState('preset'); // 'preset' or 'custom'
  const [presetRange, setPresetRange] = useState('30'); // days
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const getDateRange = () => {
    if (dateRangeType === 'custom' && customStartDate && customEndDate) {
      return {
        startDate: new Date(customStartDate).toISOString(),
        endDate: new Date(customEndDate + 'T23:59:59').toISOString()
      };
    }
    const endDate = new Date().toISOString();
    const startDate = new Date(
      Date.now() - parseInt(presetRange) * 24 * 60 * 60 * 1000
    ).toISOString();
    return { startDate, endDate };
  };

  const getDateRangeLabel = () => {
    if (dateRangeType === 'custom' && customStartDate && customEndDate) {
      return `${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()}`;
    }
    return `Last ${presetRange} days`;
  };

  const getFileName = () => {
    return `${branch?.name?.replace(/\s+/g, '_')}_Report_${formatDateToISO(new Date())}.pdf`;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();
      const days = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));

      const [branchData, overviewData, trendData, productsData] = await Promise.all([
        getBranch(branchId),
        getBranchOverview(branchId, startDate, endDate),
        getBranchTrend(branchId, 'daily', days),
        getTopProducts(branchId, 5, startDate, endDate)
      ]);

      setBranch(branchData);
      setOverview(overviewData);
      setTrend(trendData);
      setTopProducts(productsData);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [branchId, dateRangeType, presetRange, customStartDate, customEndDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Initialize custom dates to last 30 days
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    setCustomEndDate(formatDateToISO(today));
    setCustomStartDate(formatDateToISO(thirtyDaysAgo));
  }, []);

  const generatePdfReport = async () => {
    if (!branch || !overview) {
      toast.error('Dashboard data not loaded');
      return;
    }

    setGeneratingPdf(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(`${branch.name} - Performance Report`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      // Date Range
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Report Period: ${getDateRangeLabel()}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, yPos, {
        align: 'center'
      });
      yPos += 15;

      // Divider line
      doc.setDrawColor(200);
      doc.line(20, yPos, pageWidth - 20, yPos);
      yPos += 10;

      // Sales Summary Section
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Sales Summary', 20, yPos);
      yPos += 10;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const salesData = [
        ['Total Sales:', formatNumber(overview?.sales?.total_sales || 0)],
        ['Unique Customers:', formatNumber(overview?.sales?.unique_customers || 0)],
        ['Revenue:', formatCurrency(overview?.sales?.total_revenue || 0)],
        ['Profit:', formatCurrency(overview?.sales?.total_profit || 0)],
        ['Average Sale:', formatCurrency(overview?.sales?.avg_sale_value || 0)]
      ];

      salesData.forEach(([label, value]) => {
        doc.text(label, 25, yPos);
        doc.text(value, 100, yPos);
        yPos += 7;
      });
      yPos += 5;

      // Inventory Summary Section
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Inventory Summary', 20, yPos);
      yPos += 10;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const inventoryData = [
        ['Total Products:', formatNumber(overview?.inventory?.total_products || 0)],
        ['Total Units:', formatNumber(overview?.inventory?.total_units || 0)],
        ['Cost Value:', formatCurrency(overview?.inventory?.cost_value || 0)],
        ['Retail Value:', formatCurrency(overview?.inventory?.retail_value || 0)],
        ['Low Stock Items:', formatNumber(overview?.inventory?.low_stock_items || 0)],
        ['Out of Stock:', formatNumber(overview?.inventory?.out_of_stock_items || 0)]
      ];

      inventoryData.forEach(([label, value]) => {
        doc.text(label, 25, yPos);
        doc.text(value, 100, yPos);
        yPos += 7;
      });
      yPos += 5;

      // Staff Summary Section
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Staff Summary', 20, yPos);
      yPos += 10;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Staff: ${overview?.staff?.total_staff || 0}`, 25, yPos);
      yPos += 10;

      // Top Products Section
      if (topProducts.length > 0) {
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Top Products', 20, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('#', 25, yPos);
        doc.text('Product', 35, yPos);
        doc.text('Units Sold', 120, yPos);
        doc.text('Revenue', 150, yPos);
        yPos += 7;

        doc.setFont('helvetica', 'normal');
        topProducts.forEach((product, idx) => {
          doc.text(`${idx + 1}`, 25, yPos);
          doc.text(product.product_name?.substring(0, 35) || 'N/A', 35, yPos);
          doc.text(formatNumber(product.units_sold || 0), 120, yPos);
          doc.text(formatCurrency(product.revenue || 0), 150, yPos);
          yPos += 6;
        });
        yPos += 5;
      }

      // Staff Performance Section
      if (overview?.staff?.staff_performance?.length > 0) {
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Staff Performance', 20, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Staff Name', 25, yPos);
        doc.text('Sales Count', 90, yPos);
        doc.text('Total Sales', 130, yPos);
        doc.text('Avg Sale', 165, yPos);
        yPos += 7;

        doc.setFont('helvetica', 'normal');
        overview.staff.staff_performance.forEach((staff) => {
          doc.text(staff.full_name?.substring(0, 25) || 'N/A', 25, yPos);
          doc.text(formatNumber(staff.sale_count || 0), 90, yPos);
          doc.text(formatCurrency(staff.total_sales || 0), 130, yPos);
          doc.text(formatCurrency(staff.avg_sale || 0), 165, yPos);
          yPos += 6;
        });
      }

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text('Generated by BMS', pageWidth / 2, doc.internal.pageSize.getHeight() - 10, {
        align: 'center'
      });

      // Create blob and data URL for preview
      const blob = doc.output('blob');
      const dataUrl = doc.output('dataurlstring');

      setPdfBlob(blob);
      setPdfDataUrl(dataUrl);
      setShowReportModal(true);
      toast.success('Report generated!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate report');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownload = () => {
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getFileName();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Report downloaded!');
    }
  };

  const handlePrint = () => {
    if (pdfDataUrl) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head><title>${getFileName()}</title></head>
            <body style="margin:0;padding:0;">
              <iframe src="${pdfDataUrl}" style="width:100%;height:100%;border:none;" onload="setTimeout(function(){ window.print(); }, 500);"></iframe>
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    }
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(
      `${branch?.name} Performance Report - ${getDateRangeLabel()}`
    );
    const body = encodeURIComponent(
      `Please find attached the ${branch?.name} Performance Report for ${getDateRangeLabel()}.\n\nGenerated on: ${new Date().toLocaleString()}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    toast.info('Email client opened. Please attach the downloaded PDF manually.');
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      `📊 *${branch?.name} Performance Report*\n` +
        `📅 Period: ${getDateRangeLabel()}\n\n` +
        `*Sales Summary*\n` +
        `• Total Sales: ${formatNumber(overview?.sales?.total_sales || 0)}\n` +
        `• Revenue: ${formatCurrency(overview?.sales?.total_revenue || 0)}\n` +
        `• Profit: ${formatCurrency(overview?.sales?.total_profit || 0)}\n\n` +
        `*Inventory*\n` +
        `• Products: ${formatNumber(overview?.inventory?.total_products || 0)}\n` +
        `• Total Units: ${formatNumber(overview?.inventory?.total_units || 0)}\n\n` +
        `Generated: ${new Date().toLocaleString()}`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
    toast.info('Please download and share the PDF separately on WhatsApp.');
  };

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/branches')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                  {branch?.name} Dashboard
                </h1>
                <p className="text-sm text-gray-500">{branch?.branch_code}</p>
              </div>
            </div>

            {/* Date Range & Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              {/* Date Range Type Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setDateRangeType('preset')}
                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                    dateRangeType === 'preset' ? 'bg-white shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Preset
                </button>
                <button
                  onClick={() => setDateRangeType('custom')}
                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                    dateRangeType === 'custom' ? 'bg-white shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Custom
                </button>
              </div>

              {/* Preset Date Range */}
              {dateRangeType === 'preset' && (
                <select
                  value={presetRange}
                  onChange={(e) => setPresetRange(e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold text-sm"
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="365">Last year</option>
                </select>
              )}

              {/* Custom Date Range */}
              {dateRangeType === 'custom' && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="px-2 py-1.5 border rounded-lg focus:ring-2 focus:ring-accent-gold text-sm"
                    max={customEndDate || undefined}
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="px-2 py-1.5 border rounded-lg focus:ring-2 focus:ring-accent-gold text-sm"
                    min={customStartDate || undefined}
                    max={formatDateToISO(new Date())}
                  />
                </div>
              )}

              {/* Refresh Button */}
              <button
                onClick={fetchData}
                className="p-2 border rounded-lg hover:bg-gray-50"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>

              {/* PDF Report Button */}
              <button
                onClick={generatePdfReport}
                disabled={generatingPdf}
                className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm font-medium"
              >
                {generatingPdf ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    Generate Report
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
          {/* Sales Count */}
          <div className="bg-white rounded-lg shadow-sm p-4 border-2 border-blue-500">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Sales</span>
            </div>
            <p className="text-2xl font-bold text-primary-black">
              {formatNumber(overview?.sales?.total_sales)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {formatNumber(overview?.sales?.unique_customers)} customers
            </p>
          </div>

          {/* Revenue */}
          {canViewFinancials && (
            <div className="bg-white rounded-lg shadow-sm p-4 border-2 border-green-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm font-medium text-gray-600">Revenue</span>
              </div>
              <p className="text-xl font-bold text-primary-black">
                <BlurredValue value={formatCurrency(overview?.sales?.total_revenue)} />
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Profit: <BlurredValue value={formatCurrency(overview?.sales?.total_profit)} />
              </p>
            </div>
          )}

          {/* Inventory Value */}
          <div className="bg-white rounded-lg shadow-sm p-4 border-2 border-purple-500">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Inventory</span>
            </div>
            <p className="text-2xl font-bold text-primary-black">
              {formatNumber(overview?.inventory?.total_products)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {formatNumber(overview?.inventory?.total_units)} units
            </p>
          </div>

          {/* Staff */}
          <div className="bg-white rounded-lg shadow-sm p-4 border-2 border-accent-gold">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Users className="w-5 h-5 text-yellow-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Staff</span>
            </div>
            <p className="text-2xl font-bold text-primary-black">
              {formatNumber(overview?.staff?.total_staff)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Active members</p>
          </div>
        </div>

        {/* Alerts Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Low Stock Alert */}
          {overview?.inventory?.low_stock_items > 0 && (
            <div
              className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3 cursor-pointer hover:bg-yellow-100"
              onClick={() => navigate(`/branches/${branchId}/inventory?lowStock=true`)}
            >
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-800">Low Stock Alert</p>
                <p className="text-sm text-yellow-600">
                  {overview?.inventory?.low_stock_items} items need restocking
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-yellow-600 ml-auto" />
            </div>
          )}

          {/* Pending Transfers */}
          {(overview?.transfers?.incoming > 0 || overview?.transfers?.pending_approval > 0) && (
            <div
              className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3 cursor-pointer hover:bg-blue-100"
              onClick={() => navigate('/stock-transfers')}
            >
              <RefreshCw className="w-8 h-8 text-blue-600" />
              <div>
                <p className="font-semibold text-blue-800">Pending Transfers</p>
                <p className="text-sm text-blue-600">
                  {overview?.transfers?.incoming || 0} incoming,{' '}
                  {overview?.transfers?.pending_approval || 0} awaiting approval
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-blue-600 ml-auto" />
            </div>
          )}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Sales Trend */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Sales Trend
            </h3>
            {trend.length > 0 ? (
              <div className="h-48 flex items-end gap-1">
                {trend.slice(-14).map((day, idx) => {
                  const maxRevenue = Math.max(...trend.map((d) => parseFloat(d.revenue) || 0));
                  const height = maxRevenue > 0 ? (parseFloat(day.revenue) / maxRevenue) * 100 : 0;
                  return (
                    <div
                      key={idx}
                      className="flex-1 bg-gradient-to-t from-accent-gold to-yellow-400 rounded-t"
                      style={{ height: `${Math.max(height, 2)}%` }}
                      title={`${new Date(day.period).toLocaleDateString()}: ${formatCurrency(day.revenue)}`}
                    />
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No sales data available</p>
            )}
          </div>

          {/* Top Products */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-600" />
              Top Products
            </h3>
            {topProducts.length > 0 ? (
              <div className="space-y-3">
                {topProducts.map((product, idx) => (
                  <div key={product.product_id} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{product.product_name}</p>
                      <p className="text-xs text-gray-500">{product.units_sold} units sold</p>
                    </div>
                    {canViewFinancials && (
                      <span className="text-sm font-semibold text-accent-gold">
                        <BlurredValue value={formatCurrency(product.revenue)} />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No product sales data</p>
            )}
          </div>

          {/* Staff Performance */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 lg:col-span-2">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Staff Performance
            </h3>
            {overview?.staff?.staff_performance?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Staff Member</th>
                      <th className="px-4 py-2 text-center text-sm font-semibold">Sales</th>
                      {canViewFinancials && (
                        <>
                          <th className="px-4 py-2 text-right text-sm font-semibold">Total</th>
                          <th className="px-4 py-2 text-right text-sm font-semibold">Average</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {overview.staff.staff_performance.map((staff) => (
                      <tr key={staff.user_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{staff.full_name}</td>
                        <td className="px-4 py-3 text-center">{staff.sale_count}</td>
                        {canViewFinancials && (
                          <>
                            <td className="px-4 py-3 text-right">
                              <BlurredValue value={formatCurrency(staff.total_sales)} />
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500">
                              <BlurredValue value={formatCurrency(staff.avg_sale)} />
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No staff performance data</p>
            )}
          </div>
        </div>
      </div>

      {/* Report Preview Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-red-600" />
                <div>
                  <h2 className="text-lg font-bold text-gray-800">{branch?.name} Report</h2>
                  <p className="text-sm text-gray-500">{getDateRangeLabel()}</p>
                </div>
              </div>
              <button
                onClick={() => setShowReportModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* PDF Preview */}
            <div className="flex-1 overflow-auto p-4 bg-gray-100">
              {pdfDataUrl && (
                <iframe
                  src={pdfDataUrl}
                  className="w-full h-full min-h-[400px] border rounded-lg bg-white"
                  title="Report Preview"
                />
              )}
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t bg-gray-50 flex flex-wrap justify-center gap-3">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={handleEmail}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                <Mail className="w-4 h-4" />
                Email
              </button>
              <button
                onClick={handleWhatsApp}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchDashboard;
